import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	ReferenceBadge,
	Richtext,
	Text,
	TextAlignment,
	TextTag,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { motion } from 'framer-motion';
import { default as React } from 'react';
import { OfertasContext } from '../../context/ofertas-context';
import {
	checkActiveDisclaimer,
	formatCalculatedDisclaimer,
} from '../../helpers/get-disclaimer';
import {
	capitalizeSentence,
	formatPrecio,
	getDatosEconomicos,
	getFinanciacionPeriodicidad,
	getFinanciacionType,
	getPagoFinanciado,
} from '../../helpers/ofertas-helper';
import { Oferta } from '../../services/ofertas-service/models/ofertas';

export const OfertaPrice = ({
	oferta,
	handleScrollToDisclaimer,
}: {
	oferta: Oferta;
	handleScrollToDisclaimer: (reference: string) => void;
}): React.ReactElement => {
	const { disclaimerManager } = React.useContext(OfertasContext);

	const selectedDatosEconomicos = getDatosEconomicos(oferta.datosEconomicos);

	return (
		<Container
			padding={{ top: ContainerPadding.static200 }}
			verticalAlign={'flex-end'}
			wrap={'always'}
		>
			{oferta.calculatedFinance.rawResponse ? (
				<motion.div
					key={JSON.stringify(oferta.id)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1 }}
				>
					<Container
						wrap={{
							[Breakpoints.default]: 'always',
							[Breakpoints.b560]: 'never',
						}}
						verticalAlign={'flex-end'}
					>
						<Text
							textAlign={TextAlignment.end}
							tag={TextTag.span}
							appearance={TokenTextAppearance.copy100}
							color={TokenTextColor.primary}
						>
							Tu opción personalizada
						</Text>
					</Container>

					<Container verticalAlign={'flex-end'}>
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0020,
									[Breakpoints.b560]: ContainerPadding.static100,
									[Breakpoints.b960]: ContainerPadding.dynamic0020,
									[Breakpoints.b1600]: ContainerPadding.static100,
								},
							}}
							verticalAlign={'flex-end'}
							horizontalAlign={'flex-end'}
						>
							<Text
								textAlign={TextAlignment.end}
								tag={TextTag.span}
								appearance={TokenTextAppearance.headline200}
								color={TokenTextColor.primary}
							>
								{'Por\u00A0'}
							</Text>
						</Container>

						<Container wrap={'never'} verticalAlign={'flex-end'}>
							<Container wrap={'never'}>
								<Text
									tag={TextTag.span}
									appearance={TokenTextAppearance.headline200}
									color={TokenTextColor.primary}
									bold
								>
									{(oferta.calculatedFinance.summaries?.Full.Rate?.Value ||
										'') + getFinanciacionPeriodicidad(oferta.datosEconomicos)}

									<ReferenceBadge
										onClick={() => {
											const ref = checkActiveDisclaimer(
												disclaimerManager
													.getScopedDisclaimerManager()
													.getFooterDisclaimers().reference,
												formatCalculatedDisclaimer(oferta),
											)?.references?.[0].replace('.', '');
											if (ref) handleScrollToDisclaimer(ref);
										}}
										aria-label="aria-label for the reference badge"
									>
										{checkActiveDisclaimer(
											disclaimerManager
												.getScopedDisclaimerManager()
												.getFooterDisclaimers().reference,
											formatCalculatedDisclaimer(oferta),
										)?.references?.[0].replace('.', '')}
									</ReferenceBadge>
									{'\u00A0'}
								</Text>
							</Container>
						</Container>
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.dynamic0020,
									[Breakpoints.b560]: ContainerPadding.static100,
									[Breakpoints.b960]: ContainerPadding.dynamic0020,
									[Breakpoints.b1600]: ContainerPadding.static100,
								},
							}}
							verticalAlign={'flex-end'}
							horizontalAlign={'flex-end'}
						>
							<Text
								tag={TextTag.span}
								appearance={TokenTextAppearance.headline200}
								color={TokenTextColor.primary}
							>
								{'con\u00A0' +
									(getPagoFinanciado(oferta.datosEconomicos)
										? getFinanciacionType(
												getPagoFinanciado(oferta.datosEconomicos)!,
											)
										: '')}
							</Text>
						</Container>
					</Container>
				</motion.div>
			) : (
				// Contenedor que incluye todos los precios
				<Container
					wrap={'always'}
					verticalAlign={'flex-end'}
					gutter={ContainerGutter.static100}
				>
					{selectedDatosEconomicos.map((datoEconomico, index) => {
						return datoEconomico ? (
							<Container
								key={index}
								verticalAlign={'flex-end'}
								data-testid={'datoEconomico' + index}
								wrap={'always'}
							>
								{/* Precio y legal que se muestra */}
								<Container wrap={'never'} verticalAlign={'flex-end'}>
									<Container
										wrap={'never'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.dynamic0020,
												[Breakpoints.b560]: ContainerPadding.static100,
												[Breakpoints.b960]: ContainerPadding.dynamic0020,
												[Breakpoints.b1600]: ContainerPadding.static100,
											},
										}}
									>
										<Text
											tag={TextTag.span}
											appearance={TokenTextAppearance.headline200}
											color={TokenTextColor.primary}
										>
											{capitalizeSentence(datoEconomico.modalidad) + '\u00A0'}
										</Text>
										<Text
											tag={TextTag.span}
											appearance={TokenTextAppearance.headline200}
											color={TokenTextColor.primary}
											bold
										>
											{datoEconomico.periodicidad == 'pago-unico'
												? formatPrecio(datoEconomico.precio) + '€'
												: parseFloat(datoEconomico?.precio.toFixed(2))
														.toString()
														.replace('.', ',') +
													getFinanciacionPeriodicidad(oferta.datosEconomicos)}
										</Text>
										<Text sup bold>
											{datoEconomico.legal && (
												<ReferenceBadge
													onClick={() => {
														const ref = checkActiveDisclaimer(
															disclaimerManager
																.getScopedDisclaimerManager()
																.getFooterDisclaimers().reference,
															datoEconomico.legal,
														)?.references?.[0].replace('.', '');
														if (ref) handleScrollToDisclaimer(ref);
													}}
													aria-label="aria-label for the reference badge"
												>
													{checkActiveDisclaimer(
														disclaimerManager
															.getScopedDisclaimerManager()
															.getFooterDisclaimers().reference,
														datoEconomico.legal,
													)?.references?.[0].replace('.', '')}
												</ReferenceBadge>
											)}
										</Text>
										{datoEconomico.periodicidad !== 'pago-unico' && (
											<Text
												tag={TextTag.span}
												appearance={TokenTextAppearance.headline200}
												color={TokenTextColor.primary}
												bold
											>
												{'\u00A0con\u00A0' + getFinanciacionType(datoEconomico)}
											</Text>
										)}
									</Container>
								</Container>

								{datoEconomico.periodicidad == 'pago-unico' ||
								datoEconomico.detalleFinanciacion != '' ? (
									<Container
										wrap={'always'}
										verticalAlign={'flex-end'}
										horizontalAlign={'flex-start'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.dynamic0020,
												[Breakpoints.b560]: ContainerPadding.static100,
												[Breakpoints.b960]: ContainerPadding.dynamic0020,
												[Breakpoints.b1600]: ContainerPadding.static100,
											},
										}}
									>
										<Text
											tag={TextTag.span}
											appearance={TokenTextAppearance.label150}
											color={TokenTextColor.tertiary}
										>
											{datoEconomico.periodicidad == 'pago-unico' ? (
												<>Sujeto a financiación lineal</>
											) : datoEconomico.detalleFinanciacion != '' ? (
												<Richtext
													markdown={datoEconomico.detalleFinanciacion}
													dangerouslyParseRawHTML
												/>
											) : null}
										</Text>
									</Container>
								) : null}
							</Container>
						) : null;
					})}
				</Container>
			)}
		</Container>
	);
};
